'use client';

import { use, useMemo, useState } from 'react';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    Expense,
    validUnits
} from '@/lib/data';
import {
    Building2,
    Search,
    ChevronDown,
    ChevronRight,
    Receipt,
    Info,
    X,
    Filter,
    Calendar,
    ArrowDownCircle,
    CheckCircle2,
    Clock,
    UserCircle,
    Eye,
    Ban
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function ExpensePage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const baseType = initialUnit.split('-')[0];
    const sisterUnits = validUnits.filter(u =>
        u.startsWith(baseType) &&
        (u.includes('-') || !validUnits.some(v => v.startsWith(baseType) && v.includes('-')))
    );

    const defaultUnit = initialUnit.includes('-')
        ? initialUnit as SchoolUnit
        : (sisterUnits.find(u => u.includes('-')) || initialUnit) as SchoolUnit;

    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(defaultUnit);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

    const schoolData = getSchoolData(activeUnit);
    const schoolName = getSchoolName(activeUnit);

    const camelCaseUnit = activeUnit.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const expenses = (
        (schoolData as any)[`${camelCaseUnit}Expenses`] ||
        (schoolData as any)[`${activeUnit.split('-')[0]}Expenses`] ||
        []
    ) as Expense[];

    const filteredExpenses = useMemo(() => {
        return expenses.filter(e => {
            const matchesSearch = (e.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (e.account || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'All' || e.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [expenses, searchTerm, statusFilter]);

    const stats = useMemo(() => {
        return {
            total: 'Rp 14.250.000',
            pending: expenses.filter(e => e.status === 'pending').length,
            count: expenses.length
        };
    }, [expenses]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-error-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href={`/yayasan/${initialUnit}/keuangan`} className="hover:text-primary transition-all">Keuangan</Link>
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span className="text-foreground">Pengeluaran Biaya</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-error-soft/50 border border-error/10 flex items-center justify-center text-error shadow-inner">
                                    <Receipt className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Pengeluaran Biaya</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-error" /> Operasional Unit {baseType.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Unit Selector */}
                        <div className="relative min-w-[280px]">
                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block ml-1">Pilih Unit Sekolah</label>
                            <div className="relative group">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                                <select
                                    className="w-full pl-11 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all appearance-none cursor-pointer outline-none shadow-sm"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm border-l-4 border-l-error">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Pengeluaran Bulan Ini</p>
                    <div className="flex items-baseline gap-2 mt-2">
                        <p className="text-3xl font-bold text-foreground tabular-nums">{stats.total}</p>
                        <ArrowDownCircle className="w-4 h-4 text-error" />
                    </div>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-warning-soft text-warning rounded-2xl flex items-center justify-center">
                        <Clock className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Menunggu Persetujuan</p>
                        <p className="text-xl font-bold text-foreground">{stats.pending} Permintaan</p>
                    </div>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-success-soft text-success rounded-2xl flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Disetujui</p>
                        <p className="text-xl font-bold text-foreground">{stats.count - stats.pending} Biaya</p>
                    </div>
                </div>
            </section>

            {/* Expense Table */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari kategori atau akun biaya..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="bg-background border border-border-main rounded-2xl px-4 py-2.5 flex items-center gap-2">
                            <Filter className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent text-[10px] font-bold uppercase tracking-widest outline-none cursor-pointer"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="All">Semua Status</option>
                                <option value="approved">Disetujui</option>
                                <option value="pending">Menunggu</option>
                                <option value="rejected">Ditolak</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Kategori & Akun</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Metode</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main font-medium">
                            {filteredExpenses.length > 0 ? filteredExpenses.map((expense) => (
                                <tr key={expense.id} className="hover:bg-error-soft/10 transition-colors group text-sm">
                                    <td className="px-8 py-5 whitespace-nowrap text-secondary flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5 opacity-50" />
                                        {expense.date}
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <p className="font-bold text-foreground">{expense.category}</p>
                                        <p className="text-[10px] text-muted font-bold mt-0.5 uppercase tracking-widest">Acc: {expense.account}</p>
                                    </td>
                                    <td className="px-8 py-5 text-center whitespace-nowrap">
                                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">{expense.method}</span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap font-bold text-error tabular-nums">
                                        {expense.amount}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${expense.status === 'approved' ? 'bg-success-soft text-success' :
                                            expense.status === 'pending' ? 'bg-warning-soft text-warning' : 'bg-error-soft text-error'
                                            }`}>
                                            {expense.status === 'approved' ? 'Disetujui' :
                                                expense.status === 'pending' ? 'Menunggu' : 'Ditolak'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <div className="flex items-center justify-end gap-2 text-[10px] font-bold uppercase tracking-widest">
                                            {expense.status === 'pending' && (
                                                <>
                                                    <button className="px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success-dark transition-all">Setuju</button>
                                                    <button className="px-3 py-1.5 bg-error text-white rounded-lg hover:bg-error-dark transition-all">Tolak</button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => setSelectedExpense(expense)}
                                                className="p-2 rounded-xl bg-background border border-border-main text-muted hover:text-primary hover:border-primary transition-all ml-2"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-10">
                                            <Receipt className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada pengeluaran ditemukan</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedExpense && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedExpense(null)}></div>
                    <div className="relative bg-surface w-full max-w-lg rounded-4xl border border-border-main shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-error-soft/50 border border-error/10 flex items-center justify-center text-error">
                                    <Receipt className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Detail Pengeluaran</h3>
                                    <p className="text-secondary text-xs font-medium uppercase tracking-widest mt-0.5">{selectedExpense.category}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedExpense(null)} className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-foreground hover:rotate-90 transition-all duration-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-8">
                            <div className="grid grid-cols-2 gap-8">
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal</p>
                                    <p className="text-sm font-bold text-foreground">{selectedExpense.date}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Metode Bayar</p>
                                    <p className="text-sm font-bold text-foreground">{selectedExpense.method}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Akun Biaya</p>
                                    <p className="text-sm font-bold text-primary">{selectedExpense.account} • Operasional</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Status Approval</p>
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase text-success">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Terverifikasi
                                    </span>
                                </div>
                            </div>

                            <div className="p-6 bg-error-soft/30 rounded-3xl border border-error/10">
                                <p className="text-[10px] font-bold text-error uppercase tracking-widest mb-1">Total Nominal Pengeluaran</p>
                                <p className="text-3xl font-bold text-error tabular-nums tracking-tight">{selectedExpense.amount}</p>
                            </div>

                            <div className="p-6 bg-background rounded-3xl border border-border-main space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <UserCircle className="w-8 h-8 text-muted" />
                                        <div>
                                            <p className="text-[9px] font-bold text-muted uppercase tracking-widest">Dibuat Oleh</p>
                                            <p className="text-xs font-bold text-foreground">Bendahara Unit</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] font-bold text-muted uppercase tracking-widest">Waktu Input</p>
                                        <p className="text-xs font-bold text-foreground">05 Feb 2025, 09:12</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex justify-end">
                            <button onClick={() => setSelectedExpense(null)} className="px-8 py-3.5 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-background transition-all">Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
